import { FeedId } from 'features/streaming/types';
import { computeAudioImpairmentPct } from 'utils/webrtc/stats/concealment-penalty';
import { applyPcStability, pcStabilityFactor } from 'utils/webrtc/stats/pc-stability';
import {
  scoreAudioInbound,
  scoreAudioOutbound,
  scoreVideoInbound,
  scoreVideoOutbound,
  scoreCameraVideoOutbound,
  scoreFrameHealthVideoInbound,
  scoreScreenshareVideoOutbound,
} from 'utils/webrtc/stats/samba-scores';
import { WarmupTracker } from 'utils/webrtc/stats/WarmupTracker';
import { ScoringResult, StatsGraph, TrackDirection, TrackScore } from 'utils/webrtc/stats/types';

// First-observation delta is cumulative-since-track-start, inflating rates for
// tracks already streaming when polling started. 3 ticks keeps warmup ≤6s at 0.5Hz.
export const WARMUP_TICKS = 3;

export type ScoreGraphGates = {
  // False suppresses outbound-audio scores (reason='muted') so muted-DTX traffic
  // doesn't keep emitting a stale 4.x.
  sendingAudioOn: boolean;
  // Defaults "on" when broadcasting state is unknown, to avoid suppressing scores
  // during the join-time window.
  isRemoteAudioOn: (feedId: FeedId | null) => boolean;
  // True scores video without bPPPF — frame health on receive, encode intent
  // (camera) or neutral base (screenshare) on send.
  videoFrameHealthEnabled: boolean;
  isScreenshareFeed: (feedId: FeedId | null) => boolean;
  // Per-PC loss/RTT factor multiplies video scores. Audio excluded — its E-model
  // already folds loss+RTT.
  pcStabilityEnabled: boolean;
};

// Pure scoring pipeline for one PC's stats graph, lifted off `StatsOrchestrator`
// so scoring rules can be unit-tested without instantiating an orchestrator.
export function scoreGraph(
  graph: StatsGraph,
  gates: ScoreGraphGates,
  warmup: WarmupTracker,
  feedIdByMid: Record<string, FeedId>,
  sendingFeedId: FeedId | null,
  observedTrackKeysThisTick: Set<string>
): TrackScore[] {
  const feedIdFor = (mid: string, direction: TrackDirection): FeedId | null => {
    if (direction === 'sending') {
      return sendingFeedId;
    }
    return feedIdByMid[mid] ?? null;
  };

  const observe = (key: string): number => {
    observedTrackKeysThisTick.add(key);
    return warmup.observe(key);
  };

  const stability = gates.pcStabilityEnabled ? pcStabilityFactor(graph) : null;
  const stabilized = <T extends ScoringResult>(result: T): T =>
    stability ? (applyPcStability(result, stability) as T) : result;

  const out: TrackScore[] = [];

  graph.outboundAudio.forEach((fields, key) => {
    const mid = key.split('::')[1] ?? '?';
    const feedId = feedIdFor(mid, 'sending');
    const ticks = observe(key);
    let result: ReturnType<typeof scoreAudioOutbound>;
    if (ticks < WARMUP_TICKS) {
      result = { score: null, reason: 'warmup' };
    } else if (!gates.sendingAudioOn) {
      result = { score: null, reason: 'muted' };
    } else {
      result = scoreAudioOutbound(fields);
    }
    out.push({
      kind: 'audio',
      direction: 'sending',
      feedId,
      mid,
      score: result.score,
      reason: result.reason,
      limitingFactor: result.limitingFactor,
    });
  });

  graph.outboundVideo.forEach((fields, key) => {
    const [, mid, rid] = key.split('::');
    const isScreenshare = graph.pcKey === 'ss-pub';
    const ticks = observe(key);
    let result: ReturnType<typeof scoreVideoOutbound>;
    if (ticks < WARMUP_TICKS) {
      result = { score: null, reason: 'warmup' };
    } else if (gates.videoFrameHealthEnabled) {
      result = isScreenshare
        ? scoreScreenshareVideoOutbound(fields)
        : scoreCameraVideoOutbound(fields);
    } else {
      result = scoreVideoOutbound(fields);
    }
    result = stabilized(result);
    out.push({
      kind: 'video',
      direction: 'sending',
      feedId: feedIdFor(mid ?? '?', 'sending'),
      mid: mid ?? '?',
      rid: rid === 'default' ? undefined : rid,
      isScreenshare,
      score: result.score,
      reason: result.reason,
      limitingFactor: result.limitingFactor,
      qualityLimitationReason: fields.qualityLimitationReason,
      targetBitrate: fields.targetBitrate,
    });
  });

  graph.inboundAudio.forEach((fields, key) => {
    const mid = key.split('::')[1] ?? '?';
    const feedId = feedIdFor(mid, 'receiving');
    const ticks = observe(key);
    // Nulled during warmup like score, but kept on muted ticks — "muted but
    // still concealing" is a useful anomaly signal.
    let result: ReturnType<typeof scoreAudioInbound>;
    if (ticks < WARMUP_TICKS) {
      result = { score: null, reason: 'warmup' };
    } else if (!gates.isRemoteAudioOn(feedId)) {
      result = {
        score: null,
        reason: 'muted',
        audioImpairmentPct: computeAudioImpairmentPct(fields.concealment),
      };
    } else {
      result = scoreAudioInbound(fields);
    }
    out.push({
      kind: 'audio',
      direction: 'receiving',
      feedId,
      mid,
      score: result.score,
      reason: result.reason,
      limitingFactor: result.limitingFactor,
      audioImpairmentPct: result.audioImpairmentPct,
      averageJitterBufferDelayMs: fields.averageJitterBufferDelayMs,
    });
  });

  graph.inboundVideo.forEach((fields, key) => {
    const [, mid, rid] = key.split('::');
    const feedId = feedIdFor(mid ?? '?', 'receiving');
    const isScreenshare = gates.isScreenshareFeed(feedId);
    const ticks = observe(key);
    let result: ReturnType<typeof scoreVideoInbound>;
    if (ticks < WARMUP_TICKS) {
      result = { score: null, reason: 'warmup' };
    } else if (gates.videoFrameHealthEnabled) {
      result = scoreFrameHealthVideoInbound(fields);
    } else {
      result = scoreVideoInbound(fields);
    }
    result = stabilized(result);
    out.push({
      kind: 'video',
      direction: 'receiving',
      feedId,
      mid: mid ?? '?',
      rid: rid === 'default' ? undefined : rid,
      isScreenshare,
      score: result.score,
      reason: result.reason,
      limitingFactor: result.limitingFactor,
      averageJitterBufferDelayMs: fields.averageJitterBufferDelayMs,
    });
  });

  return out;
}
